/**
 * Log Review domain types.
 *
 * Covers all three Log Review screens, which share one backing service
 * (`/cag/api/v1/sirar`) and most of their vocabulary:
 *
 *   1. Log Review               — findings dashboard + compliance workflow
 *   2. Log Reviewer Insights    — team-lead review queue      (role: reviewer)
 *   3. CISO Insights / Log Review — division-head approvals    (role: approver)
 *
 * Screens 2 and 3 render the same view; only the endpoints, status
 * vocabulary and button copy differ — see LOG_INSIGHT_CONFIG below.
 */

// ─── Chart data shapes ──────────────────────────────────────────────────
// The service layer maps raw backend payloads into these before any chart
// component sees them, so a backend field rename never reaches the UI.

/** Severity vocabulary shared by the distribution panel and risk badges. */
export type Severity = "critical" | "high" | "medium" | "low" | "info" | "unknown";

/** One slice of a donut / pie chart. */
export interface PieChartDatum {
  name: string;
  value: number;
  color?: string;
}

// The ranked-bar and treemap contracts are owned by the shared chart
// components rather than redeclared here, so a change to a chart's input
// shape is a compile error in the service layer instead of a silent mismatch
// at the call site.
export type { RankedBarDatum } from "../components/ChartsComponent/RankedBarChart";
export type { TreeMapNode } from "../components/ChartsComponent/TreeMap";

export interface AreaSeries {
  name: string;
  data: number[];
  color?: string;
}

/** Multi-series trend chart, plotted over a shared category axis. */
export interface AreaChartData {
  categories: string[];
  series: AreaSeries[];
}

// ─── Main Log Review dashboard ──────────────────────────────────────────

export type LogReviewStatusTab =
  | "NonCompliantFindings"
  | "NonCompliantAcknowledgedFindings"
  | "CompliantFindings";

/**
 * A single server-session row shown in the per-CR "Details" popup that
 * opens when a reason link is clicked. One CR can span several sessions,
 * each compliant or non-compliant for its own reason.
 */
export interface LogReviewSessionDetail {
  lanUserId: string;
  serverNode: string;
  targetAccount: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  status: string;
}

/**
 * One justification → review → approval audit row shown in the per-CR
 * "Info" popup (grouped User / Log Reviewer / Log Approver columns).
 */
export interface LogReviewJustificationHistory {
  justification: string;
  justifiedBy: string;
  justifiedOn: string;
  attachments: string;
  reviewerComment: string;
  reviewerCommentedBy: string;
  reviewerCommentedOn: string;
  approverComment: string;
  approverCommentedBy: string;
  approverCommentedOn: string;
  status: string;
}

/**
 * One server / CI entry shown in the per-CR "Servers/Nodes" popup
 * (Team → Servers/CIs), opened from the Servers/Nodes count link.
 */
export interface LogReviewServerNode {
  team: string;
  serverCis: string;
}

export interface LogReviewRow {
  id: string;
  changeRequestId: string;
  application: string;
  division: string;
  userName: string;
  category: string;
  team: string;
  teamLead: string;
  risk: "Low" | "Medium" | "High";
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  servers: string;
  status: string;
  reasonForNonCompliance: string;
  serverNodes: LogReviewServerNode[];
  sessionDetails: LogReviewSessionDetail[];
  justificationHistory: LogReviewJustificationHistory[];
}

export type LogReviewMainFilterKey = "division" | "application" | "category";

export type LogReviewDrawerFilterKey = "risk" | "team" | "status" | "criticality";

export type LogReviewFilterKey = LogReviewMainFilterKey | LogReviewDrawerFilterKey;

export type LogReviewMultiFilters = Partial<Record<LogReviewFilterKey, string[]>>;

export interface LogReviewDateRange {
  from?: Date;
  to?: Date;
}

export interface LogReviewFilters {
  multi: LogReviewMultiFilters;
  dateRange?: LogReviewDateRange;
}

export interface ManageCompliancePayload {
  rowIds: string[];
  justification: string;
  attachments: File[];
}

// ─── Non-compliant distribution panel ───────────────────────────────────

export interface NonCompliantSeverityStat {
  severity: Severity;
  label: string;
  nonCompliant: number;
  acknowledged: number;
  newCount: number;
  newPercent: number;
}

export interface NonCompliantBreakdown {
  total: number;
  newCount: number;
  newPercent: number;
  severities: NonCompliantSeverityStat[];
}

// ─── Top filter-bar cascade (Division → Application → Category) ──────────

export interface LogReviewFilterApplication {
  id: number;
  appCode: string;
  appName: string;
  categories: string[];
}

export interface LogReviewFilterDivision {
  businessDivision: string;
  applications: LogReviewFilterApplication[];
}

// ─── Log Reviewer / Approver Insights ───────────────────────────────────

export type LogInsightRole = "reviewer" | "approver";

export type LogInsightRisk = "Critical" | "High" | "Medium" | "Low" | "Informational";

export interface LogInsightActionPlan {
  actionPlan: string;
  description: string;
  targetDate: string;
  requestedBy: string;
  requestedOn: string;
  comment: string;
  commentedBy: string;
  commentedOn: string;
  status: string;
}

export interface LogInsightRow {
  id: string;
  /** Backend request id (e.g. `SCR260605-0001`) — used for the attachments fetch. */
  requestId: string;
  changeRequestId: string;
  application: string;
  division: string;
  criticality: string;
  risk: LogInsightRisk;
  team: string[];
  users: string[];
  justifiedBy: string;
  justifiedOn: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  servers: string[];
  status: string;
  reasons: string[];
  attachmentCount: number;
  actionPlan: LogInsightActionPlan;
  serverNodes: LogReviewServerNode[];
  sessionDetails: LogReviewSessionDetail[];
}

export interface LogInsightDecision {
  rowIds: string[];
  decision: "positive" | "negative";
  comment: string;
}

/**
 * Per-role config for the Log Reviewer / Approver Insights pages. Both pages
 * share the same layout (3 charts + a Details table); only the workflow
 * verbs, status vocabulary and page copy differ.
 */
export interface LogInsightRoleConfig {
  /** Permission key for the future RBAC gate — see TODO in routes.ts. */
  module: string;
  title: string;
  subtitle: string;
  /** Status of rows still awaiting a decision (selectable in the table). */
  pendingStatus: string;
  /** Status applied by the positive action (Acknowledge / Approve). */
  positiveStatus: string;
  /** Status applied by the negative action (Reject). */
  negativeStatus: string;
  /** Positive action button label. */
  positiveLabel: string;
  /** Negative action button label. */
  negativeLabel: string;
  /** Confirmation modal titles. */
  positiveModalTitle: string;
  negativeModalTitle: string;
}

export const LOG_INSIGHT_CONFIG: Record<LogInsightRole, LogInsightRoleConfig> = {
  reviewer: {
    module: "log_review_insights",
    title: "Change  Assurance Governance Insights",
    subtitle: "Review and acknowledge non-compliant change request justifications.",
    pendingStatus: "Pending Review",
    positiveStatus: "Acknowledged",
    negativeStatus: "Rejected",
    positiveLabel: "Acknowledge",
    negativeLabel: "Reject",
    positiveModalTitle: "Acknowledge the Justification",
    negativeModalTitle: "Reject the Justification",
  },
  approver: {
    module: "log_review_ciso",
    title: "CISO Insights",
    subtitle: "Approve or reject acknowledged change request justifications.",
    pendingStatus: "Pending Approval",
    positiveStatus: "Approved",
    negativeStatus: "Rejected",
    positiveLabel: "Approve",
    negativeLabel: "Reject",
    positiveModalTitle: "Approve the Justification",
    negativeModalTitle: "Reject the Justification",
  },
};
